import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Heading } from "~/components/ui/heading";
import { coachQueryOptions } from "~/lib/query-options";
import { CoachLayout } from "~/components/layouts/coach-layout";
import {
  createCoachMeta,
  createServiceStructuredData,
} from "~/lib/shared/meta";

export const Route = createFileRoute("/$username/book/")({
  loader: async ({ context, params }) => {
    const coach = await context.queryClient.ensureQueryData(
      coachQueryOptions.byUsername(params.username),
    );

    if (coach == null || coach.ghlCalendarId == null) {
      throw new Response("Coach not found", { status: 404 });
    }

    return { coach };
  },
  head: ({ loaderData, params }) => {
    const coach = loaderData?.coach;
    if (!coach) {
      return {
        title: "Book a Call – Dream Coach Match",
        meta: [{ name: "description", content: "Schedule your coaching call" }],
      };
    }

    const description = `Schedule your coaching call with ${coach.fullName}. ${coach.profile?.description ? coach.profile.description.slice(0, 160) + "..." : "Professional coaching consultation to help you achieve your goals."}`;
    const imageUrl = coach.profile?.heroImageSrc || coach.profilePictureUrl;
    const currentUrl = `https://dreamcoachmatch.com/${params.username}/book`;

    const metaTags = createCoachMeta(coach.fullName, {
      description,
      ogDescription: description,
      ogImage: imageUrl || undefined,
      ogUrl: currentUrl,
      ogType: "profile",
      twitterCard: imageUrl ? "summary_large_image" : "summary",
      twitterImage: imageUrl || undefined,
      canonical: currentUrl,
      author: coach.fullName,
      keywords: [
        "coaching",
        "book",
        "consultation",
        "schedule",
        coach.profile?.coachingTitle || "life coach",
      ],
    });

    // Add structured data for the service
    const structuredData = createServiceStructuredData({
      name: `Coaching Consultation with ${coach.fullName}`,
      description,
      provider: coach.fullName,
      url: currentUrl,
      image: imageUrl || undefined,
    });

    return {
      ...metaTags,
      title: `Book a Call with ${coach.fullName} | Dream Coach Match`,
      script: [
        {
          type: "application/ld+json",
          children: JSON.stringify(structuredData),
        },
      ],
    };
  },
  component: BookingPage,
});

function BookingPage() {
  const { username } = Route.useParams();
  const { coach } = Route.useLoaderData();

  useQuery({
    ...coachQueryOptions.byUsername(username),
    initialData: coach,
  });

  useEffect(() => {
    // Load the GHL form embed script
    const script = document.createElement("script");
    script.src = "https://link.msgsndr.com/js/form_embed.js";
    script.type = "text/javascript";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      document.head.removeChild(script);
    };
  }, []);

  if (coach == null) {
    return <div>Coach not found</div>;
  }

  if (coach.ghlCalendarId == null) {
    return <div>Calendar not configured.</div>;
  }

  return (
    <CoachLayout username={username} coach={coach}>
      <section className="z-20 p-4">
        <Heading
          className="lg:mt-12"
          headline={`Schedule your call with ${coach.fullName}`}
          subheadline="Pick a date and schedule your call."
        />
        <iframe
          src={`https://api.leadconnectorhq.com/widget/booking/${coach.ghlCalendarId}`}
          className="mx-auto w-full max-w-5xl overflow-hidden"
        ></iframe>
      </section>
    </CoachLayout>
  );
}
